<template>
	<div>
		<StandardModal
			v-if="!newGroup"
			open
			:title="modalTitle"
			:confirmText="
				group ? $t('common_button_save') : $t('common_button_create')
			"
			:confirmProps="{ disabled, loading: isCreating }"
			:loading="loading"
			@confirm="saveGroup"
			@cancel="close"
			ref="newModal"
			class="new-modal"
		>
			<template #trigger>
				<slot />
			</template>
			<FormItem
				:label="$t('admin_groups_group_name_title')"
				:error="!!errors.title"
				:message="errors.title"
			>
				<Input
					v-model="title"
					autoFocus
					ref="titleInput"
					:placeholder="$t('admin_groups_group_name_placeholder')"
					:disabled="!canChangeTitle"
				>
					<template #after v-if="!canChangeTitle">
						<LockKeyholeIcon />
					</template>
				</Input>
			</FormItem>

			<FormItem
				:label="$t('admin_common_category_title')"
				:error="!!errors.categoryId"
				:message="errors.categoryId"
			>
				<Dropdown
					:options="categoryOptions"
					v-model="categoryId"
					search
					@search="categorySearch"
					:loading="isLoading"
					@load-more="loadMore"
					@change="onSelectCategory"
					:loadingMore="isLoadingMore"
					:disabled="!canChangeCategory"
					ref="categoryDropdown"
				>
					<template #actionitem>
						<Button link class="new-category" @click="launchNewCategoryModal">{{
							`+ ${$t('admin_categories_new_category_title')}`
						}}</Button>
						<div @click="categoryId = null">
							<Uncategorized />
						</div>
					</template>

					<template #selected="{ option }">
						<span
							class="category-selected"
							v-if="option"
							v-text="option.title"
						/>
						<Uncategorized v-if="!option && !categoryId && !defaultCategory" />
						<span
							class="category-fallback"
							v-if="!option && categoryId && group"
						>
							{{ group.category.title }}
						</span>
						<span class="category-fallback" v-if="!option && defaultCategory">
							{{ defaultCategory.title }}
						</span>
					</template>

					<template v-slot="{ option }">
						{{ option.title }}
					</template>

					<template v-if="noResults" #emptystate>
						<div class="empty-state">
							<div>{{ $t('common_loader_noresults_title') }}</div>
							<Button
								link
								class="new-category"
								@click="launchNewCategoryModal"
								>{{ `+ ${$t('admin_categories_new_category_title')}` }}</Button
							>
						</div>
					</template>

					<template #right-content v-if="!canChangeCategory">
						<LockKeyholeIcon />
					</template>
				</Dropdown>
			</FormItem>

			<template #footer-extra v-if="group && group.locked">
				<ActionList localize :items="lockedRecordItem" />
			</template>
		</StandardModal>

		<StandardModal
			v-if="newGroup"
			open
			:title="$t('admin_groups_new_group_button_title')"
			:confirmText="$t('common_button_done')"
			:cancelText="$t('admin_groups_create_another_group_button')"
			@confirm="close"
			@cancel="openNewModal"
			@close="close"
			ref="successModal"
			class="success-modal"
		>
			<div class="success-top-content">
				<GroupsIcon halo />
				<div class="success-top-title">
					{{
						$t('admin_common_added_success_label', {
							GroupName: newGroup.title,
						})
					}}
				</div>
			</div>
			<div class="success-actions">
				<div class="success-actions-label" v-t="'admin_groups_next_steps'" />
				<RouterLink
					class="success-action-item"
					v-for="action in successActions"
					:key="action.id"
					:to="action.to"
					@click="close"
				>
					<div>
						<component :is="action.icon" />
						{{ $t(action.text) }}
					</div>
					<div class="action-link" v-text="$t(action.linkText)" />
				</RouterLink>
			</div>
		</StandardModal>

		<NewCategoryModal
			v-if="showNewCategoryModal"
			@create="newCategory"
			@close="showNewCategoryModal = false"
			ref="newCategoryModal"
		/>
	</div>
</template>

<script>
import {
	Input,
	StandardModal,
	FormItem,
	Dropdown,
	Button,
} from '@/components/common'
import { GroupsIcon, DetailsIcon, LockKeyholeIcon } from '@/icons'
import { getOrganization } from '@/store'
import NewCategoryModal from '../Categories/NewCategoryModal'
import { get, post, patch, ActionList, Uncategorized } from '../common'
import debounce from 'debounce'
import { storage } from '@/helpers/utils'

export default {
	name: 'NewGroupModal',
	props: {
		group: Object,
		loading: { type: Boolean, default: null },
		defaultCategoryId: null,
	},
	components: {
		StandardModal,
		FormItem,
		Input,
		GroupsIcon,
		Dropdown,
		Button,
		NewCategoryModal,
		ActionList,
		Uncategorized,
		LockKeyholeIcon,
	},
	data() {
		return {
			title: this.group?.title ?? null,
			newGroup: null,
			categories: [],
			categoryId: this.group?.category_id ?? this.defaultCategoryId ?? null,
			isLoading: false,
			isLoadingMore: false,
			showNewCategoryModal: false,
			isCreating: false,
			defaultCategory: null,
			categoryQuery: null,
			errors: {},
		}
	},

	emits: ['success', 'close'],

	created() {
		this.fetchCategoriesDebounced = debounce(
			this.fetchCategories.bind(this),
			500
		)
	},

	async mounted() {
		await this.$nextTick()
		this.$refs.titleInput.focus()
		if (this.canChangeCategory) {
			await this.fetchCategories()
			if (this.defaultCategoryId) {
				this.defaultCategory = this.categories.find(
					(c) => c.id === this.defaultCategoryId
				)
				if (!this.defaultCategory) {
					await this.fetchDefaultCategory()
				}
			}
		}
	},

	computed: {
		trimmedTitle() {
			return this.title?.trim()
		},
		disabled() {
			if (this.group) {
				const { changetitle, changecategory } = this.group
				if ([changetitle, changecategory].every((x) => x === 'system')) {
					// user does not have permission to edit this group
					return true
				}
				if (
					this.title === this.group.title &&
					this.group.category_id === this.categoryId
				) {
					// form fields have not been changed
					return true
				}
			}
			return !this.trimmedTitle
		},

		successActions() {
			const to = { name: 'group', params: { id: this.newGroup.id } }
			return [
				{
					icon: GroupsIcon,
					text: 'admin_groups_add_people_to_group_button',
					linkText: 'admin_groups_add_people_button',
					to: { ...to, query: { add: true } },
				},
				{
					icon: DetailsIcon,
					text: 'admin_groups_view_group_details',
					linkText: 'admin_groups_open_details_button',
					to,
				},
			]
		},

		lockedRecordItem() {
			return [
				{
					id: 'locked',
					icon: LockKeyholeIcon,
					type: 'primary',
					label: 'admin_common_locked_record_label',
					noClick: true,
				},
			]
		},

		modalTitle() {
			return this.group
				? this.$t('admin_groups_edit_group_button')
				: this.$t('admin_groups_new_group_button_title')
		},

		noResults() {
			return !this.isLoading && !this.isLoadingMore && !this.categories.length
		},

		canChangeTitle() {
			return !this.group || this.group.changetitle !== 'system'
		},

		canChangeCategory() {
			return !this.group || this.group.changecategory !== 'system'
		},

		categoryOptions() {
			const options = []
			if (this.selectedCategory) {
				// need to include the selected category in the options so it
				// can appear in the selected state of the dropdown
				options.push(this.selectedCategory)
			}
			options.push(...this.categories)
			return options
		},
	},

	methods: {
		launchNewCategoryModal() {
			this.showNewCategoryModal = true
		},

		onSelectCategory(categoryId) {
			this.selectedCategory = this.categories.find((c) => c.id === categoryId)
		},

		async saveGroup() {
			this.modalError?.()
			this.errors = {}
			this.isCreating = true

			const payload = {
				title: this.title,
				organization_id: getOrganization().id,
				category_id: this.categoryId || null,
			}

			try {
				if (this.group) {
					const updated = await patch(
						`/organization/groups/${this.group.id}`,
						payload
					)
					this.$alert.success(this.$t('common_confirmation_updated_nocheck'))
					this.$emit('success', updated)
				} else {
					this.newGroup = await post('/organization/groups', payload)
					this.$emit('success', this.newGroup)
				}
				storage.set('org_mgmt_changed_groups', true)
				this.title = null
				this.categoryId = null
			} catch (e) {
				this.displayErrors(e)
			}
			this.isCreating = false
		},

		displayErrors(e) {
			console.error(e)
			if (!e.response?.data?.errors) {
				this.modalError = this.$refs.newModal.$alert.error()
				return
			}
			this.errors = Object.entries(e.response?.data?.errors).reduce(
				(acc, [key, errors]) => {
					acc[key] = errors[0].message
					return acc
				},
				{}
			)
		},

		close() {
			this.title = null
			this.newGroup = null
			this.categoryId = null
			this.error?.()
			this.$emit('close')
		},

		openNewModal() {
			this.categoryId = this.newGroup.category_id
			this.newGroup = null
		},

		newCategory(category) {
			this.categoryId = category.id
			if (this.group) {
				// eslint-disable-next-line vue/no-mutating-props
				this.group.category = category
			}
			this.fetchCategories()
			this.$nextTick(() => {
				this.$refs.titleInput.focus()
			})
		},

		async fetchDefaultCategory() {
			this.isLoading = true
			try {
				const resp = await get(
					`/organization/categories/${this.defaultCategoryId}`
				)
				this.defaultCategory = resp.data
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.isLoading = false
		},

		async fetchCategories() {
			this.isLoading = true
			const params = { sort: 'title' }

			if (this.categoryQuery) {
				params['filter[search]'] = this.categoryQuery
			}

			try {
				this.apiResponse = await get('/organization/categories', params)
				this.categories = this.apiResponse.data
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.isLoading = false
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.isLoadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.categories = this.categories.concat(this.apiResponse.data)
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.isLoadingMore = false
		},

		categorySearch(categoryQuery) {
			this.categoryQuery = categoryQuery || null
			this.isLoading = true
			this.fetchCategoriesDebounced()
		},
	},
}
</script>

<style lang="scss">
@import 'src/sass/variables';

.new-modal {
	.modal-body {
		padding: 24px;
	}

	.form-item {
		padding: 24px 24px 0;
	}

	.dropdown-container {
		.dropdown-selected {
			background: #fafafa;
		}
		.new-category {
			font-size: 16px;
		}
		.uncategorized {
			color: var(--neutral-800);
		}
	}

	.dropdown-options-container {
		&.open {
			min-height: 290px;
		}
		.scroller {
			max-height: 244px !important;
		}
	}

	.pronto-input {
		.after {
			.pronto-icon {
				color: var(--neutral-400);
				margin-right: 12px;
			}
		}
	}

	.empty-state {
		@include absolute-cover;
		@include flex-center;
		background-color: white;
		flex-direction: column;
		top: 45px;
	}
}

.success-modal {
	.modal-body {
		padding: 24px;
		.success-top-content {
			@include flex-column;
			align-items: center;
			margin: 56px 0;
			.success-top-title {
				font-family: Avenir-Heavy;
				font-size: 24px;
			}
			.pronto-icon {
				font-size: 55px;
				color: var(--blue-300);
				margin-bottom: 24px;
			}
		}
		.success-actions {
			.success-actions-label {
				font-family: Avenir-Heavy;
				padding: 0 16px;
				margin-bottom: 8px;
			}
			.success-action-item {
				@include flex-spread;
				height: 50px;
				border-radius: 6px;
				padding: 0 16px;
				cursor: pointer;
				margin-bottom: 8px;
				color: inherit;
				text-decoration: none;
				&:hover {
					background: rgba($pronto-blue, 0.1);
				}
				> div {
					@include flex;
				}
				.pronto-icon {
					color: var(--blue-300);
					font-size: 20px;
					margin-right: 12px;
				}
				.action-link {
					color: var(--blue-300);
				}
			}
		}
	}
}
</style>
